import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getJobOrder, putJobOrder, patchJobOrderLink } from "../API/jobOrderAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import TransmittalNotesCard from "./FormComponents/TransmittalNotesCard";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_DESCRIPTIONS, JOB_TYPES } from "../JobOrders/utils/JobOrderConstants";

function EditTransmittalForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [jobOrder, setJobOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes("operations_technician");

  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo");

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage("Error fetching job order: " + (err.message || err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder();
  }, [id]);

  const handleInputChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)])
      );
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  const buildClientSignatureIsoFromEditedTime = (hhmm) => {
    if (!hhmm) return jobOrder?.client_signature_uploaded_at || "";

    const [hour, minute] = String(hhmm).split(":").map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return jobOrder?.client_signature_uploaded_at || "";
    }

    try {
      const baseIso = jobOrder?.client_signature_uploaded_at;
      const base = baseIso
        ? new Date(baseIso)
        : new Date(`${jobOrder?.job_date}T00:00:00+08:00`);

      if (Number.isNaN(base.getTime())) {
        return jobOrder?.client_signature_uploaded_at || "";
      }

      const year = base.getUTCFullYear();
      const month = base.getUTCMonth();
      const date = base.getUTCDate();

      const rebuilt = new Date(Date.UTC(year, month, date, hour - 8, minute, 0));
      return rebuilt.toISOString();
    } catch {
      return jobOrder?.client_signature_uploaded_at || "";
    }
  };

  const buildUpdatedParams = () => {
    const techNames = (jobOrder?.technicians || []).map(
      (t) => `${t.firstname} ${t.lastname}`
    );

    return {
      status: jobOrder?.status || "",
      job_date: jobOrder?.job_date || "",
      start_time: jobOrder?.start_time || "",
      end_time: jobOrder?.end_time || "",
      ...(usesClientSignatureAsEndTime
        ? {
            client_signature_uploaded_at:
              jobOrder?.client_signature_uploaded_at || "",
          }
        : {}),
      transmittal_notes: jobOrder?.transmittal_notes || "",
      accomplished_by_technician: techNames,
      technician_signatory: jobOrder?.technician_signatory || "",
      acknowledgedClientSignatory:
        jobOrder?.acknowledgedClientSignatory ||
        jobOrder?.acknowledged_client_signatory ||
        "",
      accomplishedSignature:
        jobOrder?.accomplishedSignature || jobOrder?.accomplished_signature || "",
      acknowledgedSignature:
        jobOrder?.acknowledgedSignature || jobOrder?.acknowledged_signature || "",
      satisfaction_rating: jobOrder?.satisfaction_rating || "",
      client_satisfaction_remarks:
        jobOrder?.client_satisfaction_remarks || "",
    };
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const updatedJobOrderParameters = buildUpdatedParams();

      const payload = convertBooleans({
        jobOrder,
        updatedJobOrderParameters,
        form: updatedJobOrderParameters,
      });

      await submitDocumentFieldMapper(payload);

      const generatedLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: false,
        includeCertificate: false,
        includeReceipt: true,
      });

      await patchJobOrderLink(jobOrder.id, generatedLinks);
      await putJobOrder(jobOrder.id, {
        ...updatedJobOrderParameters,
        ...generatedLinks,
      });

      setJobOrder((prev) => ({
        ...prev,
        ...updatedJobOrderParameters,
        ...generatedLinks,
      }));

      setSuccessMessage(
        "Service report updated and documents regenerated successfully."
      );
    } catch (error) {
      console.error("Error updating job order:", error);
      setErrorMessage("Failed to update transmittal form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder?.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) =>
      (jobOrder?.job_description || []).includes(formatKey(label))
    )
    .sort((a, b) => a.localeCompare(b));

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const usesClientSignatureAsEndTime = Boolean(jobOrder?.client_signature_uploaded_at);

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    transmittal_notes: "Transmittal Notes Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) =>
    jobOrder?.[`${stepKey}_completed_at`] || latestStepLogMap?.[stepKey] || "";

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: false,
      includeCertificate: false,
      includeReceipt: true,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      isLoading={isLoading}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
    >
      <Grid container>
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={jobOrder?.client || {}}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              status: value,
            }))
          }
          showStatus={true}
          readOnly={false}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        <TransmittalNotesCard
          notes={jobOrder?.transmittal_notes || ""}
          onNotesChange={(val) => handleInputChange("transmittal_notes", val)}
          readOnly={false}
          title="Transmittal Notes"
          rows={6}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.transmittal_notes}
              value={getStepTimestamp("transmittal_notes")}
              colorSet="info"
            />
          </Grid>
        )}

        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
          editableSchedule={true}
          onStartTimeChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              start_time: value,
            }))
          }
          onEndTimeChange={(value) =>
            setJobOrder((prev) => ({
              ...prev,
              end_time: value,
              ...(usesClientSignatureAsEndTime
                ? {
                    client_signature_uploaded_at:
                      buildClientSignatureIsoFromEditedTime(value),
                  }
                : {}),
            }))
          }
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}

        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            sx={{
              fontSize: "0.875rem",
            }}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default EditTransmittalForm;